import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, CheckCircle2, ExternalLink, Loader2, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/aiToolkit";
import type { CreateClickUpTaskRequest } from "@/types/clickup";
import {
  useClickUpLists,
  useClickUpMembers,
  useClickUpTeams,
  useCreateClickUpTasks,
} from "@/hooks/useClickUp";
import { shouldAutoPostClickUp } from "@/lib/agentActionRouter";
import type { ResolvedClickUpMeeting } from "@/lib/clickup/resolveClickUpProject";
import type { AgentAnalysisResult } from "@/types/communications";
import type { AgentHandoffSource } from "@/types/agentHandoff";
import type { AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import { DiscussInChatButton } from "@/components/communications/DiscussInChatButton";

interface AgentClickUpResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent | null;
  teamId: string;
  meetings: ResolvedClickUpMeeting[];
  handoffSource?: AgentHandoffSource;
  returnPath?: string;
  items?: AnalyzeItemInput[];
  results?: AgentAnalysisResult[];
  onIndexForRag?: () => Promise<void>;
  /** When true, never auto-post (e.g. Telegram scope requires user confirmation). */
  forceManualPost?: boolean;
}

type PostedTask = {
  itemId: string;
  taskName: string;
  url?: string;
  error?: string;
};

export function AgentClickUpResultsDialog({
  open,
  onOpenChange,
  agent,
  teamId: initialTeamId,
  meetings: initialMeetings,
  handoffSource = "fireflies",
  returnPath = "/fireflies",
  items = [],
  results = [],
  onIndexForRag,
  forceManualPost = false,
}: AgentClickUpResultsDialogProps) {
  const { toast } = useToast();
  const [meetings, setMeetings] = useState(initialMeetings);
  const [selectedTeamId, setSelectedTeamId] = useState(initialTeamId);
  const [autoPosted, setAutoPosted] = useState(false);
  const [postedTasks, setPostedTasks] = useState<PostedTask[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: teams = [], isLoading: teamsLoading } = useClickUpTeams(open);
  const { data: lists = [], isLoading: listsLoading } = useClickUpLists(
    selectedTeamId,
    open && !!selectedTeamId
  );
  const { data: members = [] } = useClickUpMembers(selectedTeamId, open && !!selectedTeamId);
  const createTasksMutation = useCreateClickUpTasks();

  useEffect(() => {
    if (open) {
      setMeetings(initialMeetings);
      setSelectedTeamId(initialTeamId);
      setAutoPosted(false);
      setPostedTasks([]);
    }
  }, [open, initialMeetings, initialTeamId]);

  const selectedDeliverableCount = useMemo(
    () =>
      meetings.reduce(
        (sum, m) => sum + m.proposedDeliverables.filter((d) => d.selected).length,
        0
      ),
    [meetings]
  );

  const primaryProjectName = useMemo(() => {
    const names = [...new Set(meetings.map((m) => m.matchedListName))];
    return names.length === 1 ? names[0] : "ClickUp";
  }, [meetings]);

  const toggleDeliverable = useCallback(
    (meetingId: string, index: number, selected: boolean) => {
      setMeetings((prev) =>
        prev.map((m) => {
          if (m.itemId !== meetingId) return m;
          const proposedDeliverables = m.proposedDeliverables.map((d, i) =>
            i === index ? { ...d, selected } : d
          );
          return { ...m, proposedDeliverables };
        })
      );
    },
    []
  );

  const updateDeliverableTitle = useCallback(
    (meetingId: string, index: number, title: string) => {
      setMeetings((prev) =>
        prev.map((m) => {
          if (m.itemId !== meetingId) return m;
          const proposedDeliverables = m.proposedDeliverables.map((d, i) =>
            i === index ? { ...d, title } : d
          );
          return { ...m, proposedDeliverables };
        })
      );
    },
    []
  );

  const updateMeetingList = useCallback((meetingId: string, listId: string) => {
    const list = lists.find((l) => l.id === listId);
    setMeetings((prev) =>
      prev.map((m) =>
        m.itemId === meetingId
          ? {
              ...m,
              matchedListId: listId,
              matchedListName: list?.name ?? m.matchedListName,
            }
          : m
      )
    );
  }, [lists]);

  const buildTaskRequests = useCallback((): CreateClickUpTaskRequest[] => {
    const requests: CreateClickUpTaskRequest[] = [];
    for (const meeting of meetings) {
      for (const deliverable of meeting.proposedDeliverables) {
        if (!deliverable.selected) continue;
        const ownerLine = deliverable.owner ? `**Owner:** ${deliverable.owner}\n` : "";
        const dueLine = deliverable.dueDate ? `**Due:** ${deliverable.dueDate}\n` : "";
        const priorityLine = deliverable.priority ? `**Priority:** ${deliverable.priority}\n` : "";
        requests.push({
          listId: meeting.matchedListId,
          name: deliverable.title.trim() || "Untitled task",
          markdown_description: [
            ownerLine,
            dueLine,
            priorityLine,
            deliverable.description?.trim() ?? "",
            `\n_From meeting: ${meeting.itemLabel}_`,
          ]
            .filter(Boolean)
            .join("\n"),
          assignees: [],
          checklistItems: [],
        });
      }
    }
    return requests;
  }, [meetings]);

  const handlePost = useCallback(async () => {
    const tasks = buildTaskRequests();
    if (tasks.length === 0) {
      toast({
        title: "No tasks selected",
        description: "Select at least one deliverable to post.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const results = await createTasksMutation.mutateAsync(tasks);
      const posted: PostedTask[] = [];
      let taskIndex = 0;

      for (const meeting of meetings) {
        for (const deliverable of meeting.proposedDeliverables) {
          if (!deliverable.selected) continue;
          const result = results[taskIndex++];
          posted.push({
            itemId: meeting.itemId,
            taskName: deliverable.title,
            url: result.success ? result.result.url : undefined,
            error: result.success ? undefined : result.error,
          });
        }
      }

      setPostedTasks(posted);
      const succeeded = posted.filter((p) => p.url);
      const failed = posted.filter((p) => p.error);

      if (succeeded.length > 0) {
        toast({
          title: "Posted to ClickUp",
          description: `${succeeded.length} task${succeeded.length !== 1 ? "s" : ""} created in ${primaryProjectName}.`,
        });
      }
      if (failed.length > 0) {
        toast({
          title: "Some tasks failed",
          description: failed.map((f) => f.error).filter(Boolean).join("; "),
          variant: "destructive",
        });
      }
    } catch (e) {
      toast({
        title: "ClickUp post failed",
        description: e instanceof Error ? e.message : "Could not create tasks",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [buildTaskRequests, createTasksMutation, meetings, primaryProjectName, toast]);

  useEffect(() => {
    if (!open || autoPosted || !agent || postedTasks.length > 0 || forceManualPost) return;
    if (!shouldAutoPostClickUp(agent)) return;
    if (selectedDeliverableCount === 0) return;
    setAutoPosted(true);
    void handlePost();
  }, [open, autoPosted, agent, postedTasks.length, selectedDeliverableCount, handlePost]);

  const isBusy = isSubmitting || createTasksMutation.isPending;
  const showPostedSummary = postedTasks.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] !grid grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 px-6 pt-6 pb-3">
          <DialogTitle>Deliverables → ClickUp</DialogTitle>
          <DialogDescription>
            {agent?.name ?? "Agent"} extracted tasks and matched them to your ClickUp projects.
            {shouldAutoPostClickUp(agent) && !showPostedSummary && " Automatic mode — posting tasks…"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 text-sm shrink-0 px-6 pb-3">
          <span className="text-muted-foreground">Team:</span>
          <Select
            value={selectedTeamId}
            onValueChange={setSelectedTeamId}
            disabled={teamsLoading || isBusy}
          >
            <SelectTrigger className="h-8 w-48">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              {teams.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {members.length > 0 && (
            <Badge variant="outline">{members.length} members</Badge>
          )}
        </div>

        <div className="min-h-0 overflow-y-auto overscroll-contain px-6 py-2">
          <div className="space-y-6 pb-2">
            {meetings.map((meeting) => (
              <div key={meeting.itemId} className="rounded-lg border p-4 space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="font-medium">{meeting.itemLabel}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Detected project: <strong>{meeting.detectedProject.name}</strong>
                      {" · "}
                      {Math.round(meeting.detectedProject.confidence * 100)}% confidence
                    </p>
                    <p className="text-xs text-muted-foreground">{meeting.detectedProject.reason}</p>
                  </div>
                  <div className="min-w-[200px]">
                    <label className="text-xs text-muted-foreground">Target list</label>
                    <Select
                      value={meeting.matchedListId}
                      onValueChange={(v) => updateMeetingList(meeting.itemId, v)}
                      disabled={listsLoading || isBusy}
                    >
                      <SelectTrigger className="h-8 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {lists.map((l) => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.path || l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {meeting.relatedExistingTasks.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Related existing tasks</h4>
                    <div className="space-y-1">
                      {meeting.relatedExistingTasks.map((task) => (
                        <div
                          key={task.id}
                          className="flex items-center justify-between gap-2 text-sm rounded-md bg-muted/40 px-2 py-1.5"
                        >
                          <div className="min-w-0">
                            <span className="truncate">{task.name}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {task.reason}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge variant="secondary" className="text-xs">
                              {Math.round(task.relevance * 100)}%
                            </Badge>
                            <a
                              href={task.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Proposed new tasks</h4>
                  {meeting.proposedDeliverables.map((deliverable, index) => (
                    <div
                      key={`${meeting.itemId}-${index}`}
                      className="rounded-md border p-3 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={deliverable.selected}
                          disabled={isBusy}
                          onCheckedChange={(checked) =>
                            toggleDeliverable(meeting.itemId, index, checked === true)
                          }
                        />
                        <div className="flex-1 space-y-2">
                          <Input
                            value={deliverable.title}
                            disabled={isBusy}
                            onChange={(e) =>
                              updateDeliverableTitle(meeting.itemId, index, e.target.value)
                            }
                          />
                          {deliverable.description && (
                            <Textarea
                              value={deliverable.description}
                              readOnly
                              className="min-h-[60px] text-sm"
                            />
                          )}
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            {deliverable.owner && <span>Owner: {deliverable.owner}</span>}
                            {deliverable.dueDate && <span>Due: {deliverable.dueDate}</span>}
                            {deliverable.priority && <span>Priority: {deliverable.priority}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {showPostedSummary && (
              <div className="rounded-lg border border-green-500/40 bg-green-500/5 p-4 space-y-2">
                <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Posted to ClickUp
                </div>
                {postedTasks.map((task, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span>{task.taskName}</span>
                    {task.url ? (
                      <a
                        href={task.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : (
                      <span className="text-destructive inline-flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {task.error ?? "Failed"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0 px-6 py-4 border-t bg-background">
          <DiscussInChatButton
            agent={agent}
            source={handoffSource}
            returnPath={returnPath}
            items={items}
            results={results}
            actionType="clickup"
            clickUpContext={
              agent
                ? { agent, teamId: selectedTeamId, meetings }
                : null
            }
            onIndexForRag={onIndexForRag}
            disabled={isBusy}
          />
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
            Close
          </Button>
          {!showPostedSummary && (
            <Button onClick={() => void handlePost()} disabled={isBusy || selectedDeliverableCount === 0}>
              {isBusy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Post to {primaryProjectName} ({selectedDeliverableCount})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
